/**
 * pages/FleetManager.jsx — Fleet Inventory Page
 *
 * Vehicle grid with filter bar, search, compliance indicators.
 * Uses ui.jsx components + CSS variables exclusively.
 * Fetches from GET /api/fleet/vehicles + GET /api/fleet/summary.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fleet as fleetApi } from '../services/api';
import { formatShortDate, formatNumber } from '../utils/formatters';
import { PageHeader, Badge, Card, SearchInput, EmptyState, SkeletonCard } from '../components/ui';
import { getCarIcon } from '../components/CarIcons';
import { vehicleImageMap } from '../utils/vehicleImageMap';

// ── Status config ────────────────────────────────────────────────────
const STATUS_CONFIG = {
  available:   { labelKey: 'fleet.available',   variant: 'success' },
  rented:      { labelKey: 'fleet.inRental',    variant: 'info' },
  maintenance: { labelKey: 'fleet.maintenance', variant: 'warning' },
  reserved:    { labelKey: 'fleet.reserved',    variant: 'warning' },
  police_hold: { labelKey: 'fleet.policeHold',  variant: 'danger' },
  retired:     { labelKey: 'fleet.retired',     variant: 'neutral' },
};

// ── Filter pills ─────────────────────────────────────────────────────
const FILTERS = [
  { key: 'all',         labelKey: 'fleet.all' },
  { key: 'available',   labelKey: 'fleet.available' },
  { key: 'rented',      labelKey: 'fleet.rented' },
  { key: 'maintenance', labelKey: 'fleet.maintenance' },
  { key: 'reserved',    labelKey: 'fleet.reserved' },
];

// ── Compliance dot helper ────────────────────────────────────────────
function complianceColor(dateStr) {
  if (!dateStr) return 'var(--text-muted)';
  const diff = (new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24);
  if (diff < 0) return 'var(--red)';
  if (diff < 30) return 'var(--orange)';
  return 'var(--green)';
}

// ── Filter pill button ───────────────────────────────────────────────
function FilterPill({ label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px',
        fontSize: 'var(--text-caption)',
        fontWeight: active ? 600 : 400,
        background: active ? 'var(--gold-dim)' : 'transparent',
        color: active ? 'var(--gold)' : 'var(--text-secondary)',
        border: `1px solid ${active ? 'var(--border-gold)' : 'var(--border)'}`,
        borderRadius: 20,
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        transition: `all var(--duration-fast) var(--ease-smooth)`,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      {count !== undefined && (
        <span style={{
          fontSize: 10,
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          color: active ? 'var(--gold-light)' : 'var(--text-muted)',
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

// ── Vehicle Card ─────────────────────────────────────────────────────
function VehicleCard({ vehicle, index = 0 }) {
  const { t } = useTranslation();
  const status = STATUS_CONFIG[vehicle.status] || STATUS_CONFIG.retired;
  const rate = vehicle.daily_price_mad || vehicle.prix_jour;
  const hasRate = rate && typeof rate === 'number';

  return (
    <div style={{
      animation: `scaleIn 300ms var(--ease-smooth) both`,
      animationDelay: `${index * 50}ms`,
    }}>
      <Card hoverable style={{ padding: 0, overflow: 'hidden' }}>
        {/* 1. IMAGE BLOCK — Top, wide, landscape */}
        <div style={{
          width: '100%',
          height: 240,
          background: 'var(--elevated)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          borderBottom: '1px solid var(--border)',
        }}>
          {vehicleImageMap[vehicle.id] ? (
            <img 
              src={vehicleImageMap[vehicle.id]} 
              alt={`${vehicle.make} ${vehicle.model}`}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                padding: 6,
              }}
            />
          ) : (
            <div style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              {getCarIcon(vehicle.category, { color: 'var(--text-muted)', size: 96 })}
            </div>
          )}
        </div>

        {/* 2. INFO SECTION — All below image */}
        <div style={{ padding: 'var(--space-xl)' }}>
          {/* Title + Status Row */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            justifyContent: 'space-between',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-xs)',
          }}>
            <div style={{
              fontSize: 'var(--text-title)',
              fontWeight: 600,
              color: 'var(--text)',
              lineHeight: 1.3,
              flex: 1,
            }}>
              {vehicle.make} {vehicle.model}
            </div>
            <Badge label={t(status.labelKey)} variant={status.variant} />
          </div>

          {/* Year + Category */}
          <div style={{
            fontSize: 'var(--text-body)',
            color: 'var(--text-secondary)',
            marginBottom: 'var(--space-sm)',
          }}>
            {vehicle.year} · {vehicle.category || '—'}
          </div>

          {/* Plate Number */}
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            fontSize: 'var(--text-body)',
            color: 'var(--gold)',
            letterSpacing: 1.5,
            marginBottom: 'var(--space-lg)',
          }}>
            {vehicle.plate_number}
          </div>

          {/* Metrics Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--space-md)',
            marginBottom: 'var(--space-lg)',
          }}>
            <StatItem 
              label={t('fleet.mileage')} 
              value={vehicle.current_mileage ? formatNumber(vehicle.current_mileage) : '0'} 
              suffix="km" 
            />
            <StatItem 
              label={t('fleet.dailyRate')} 
              value={hasRate ? formatNumber(rate) : t('fleet.notSet') || '—'} 
              suffix={hasRate ? 'MAD' : null}
              mono={hasRate}
            />
          </div>

          {/* Divider + Compliance Chips */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-sm)',
            paddingTop: 'var(--space-md)',
            borderTop: '1px solid var(--border)',
          }}>
            <ComplianceChip label={t('fleet.insurance')} date={vehicle.insurance_expiry} />
            <ComplianceChip label={t('fleet.techVisit')} date={vehicle.tech_visit_expiry} />
            <ComplianceChip label={t('fleet.vignette')} date={vehicle.vignette_expiry} />
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── Stat item inside vehicle card ────────────────────────────────────
function StatItem({ label, value, suffix, mono }) {
  return (
    <div>
      <div style={{
        fontSize: 'var(--text-label)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 2,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 'var(--text-body)',
        fontWeight: 500,
        color: 'var(--text)',
        fontFamily: mono ? 'var(--font-mono)' : 'inherit',
      }}>
        {value}
        {suffix && <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginLeft: 3 }}>{suffix}</span>}
      </div>
    </div>
  );
}

// ── Compliance chip (wrappable pill) ─────────────────────────────────
function ComplianceChip({ label, date }) {
  const color = complianceColor(date);
  const formatted = date ? formatShortDate(date) : '—';

  return (
    <div style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: 6,
      padding: '4px 10px',
      background: 'var(--elevated)',
      borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--border)',
    }}>
      <span style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: color,
        display: 'inline-block',
        flexShrink: 0,
      }} />
      <span style={{
        fontSize: 'var(--text-label)',
        color: 'var(--text-secondary)',
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}>
        {label}
      </span>
      <span style={{
        fontSize: 'var(--text-label)',
        color: 'var(--text)',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}>
        {formatted}
      </span>
    </div>
  );
}

// ── Add Vehicle Modal ────────────────────────────────────────────────
const INITIAL_FORM = {
  plate_number: '', make: '', model: '', year: new Date().getFullYear(),
  category: 'economy', couleur: '', prix_jour: '',
  insurance_expiry: '', tech_visit_expiry: '', vignette_expiry: '',
};

function AddVehicleModal({ open, onClose, onSubmit, submitting, apiReachable = true }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    setError(null);
    if (!form.plate_number.trim() || !form.make.trim() || !form.model.trim()) {
      setError(t('fleet.plateRequired'));
      return;
    }
    try {
      await onSubmit(form);
      setForm({ ...INITIAL_FORM });
    } catch (e) {
      setError(e.message || t('fleet.createError'));
    }
  };

  if (!open) return null;

  const inputStyle = (focused) => ({
    width: '100%',
    height: 44,
    padding: '0 12px',
    background: 'var(--elevated)',
    border: `1px solid var(--border)`,
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text)',
    fontSize: 'var(--text-body)',
    fontFamily: 'inherit',
    outline: 'none',
  });

  const labelStyle = {
    fontSize: 'var(--text-label)',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 4,
    display: 'block',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'fadeIn var(--duration-fast) var(--ease-smooth)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 480, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto',
          background: 'var(--card)',
          border: '1px solid var(--border-medium)',
          borderRadius: 'var(--radius)',
          animation: 'scaleIn var(--duration-base) var(--ease-smooth)',
          boxShadow: 'var(--shadow-elevated)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: 'var(--space-xl)',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 'var(--text-title)', fontWeight: 600, color: 'var(--text)' }}>
              {t('fleet.addVehicle')}
            </div>
            <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginTop: 2 }}>
              {t('fleet.addVehicleDesc')}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: 'var(--text-muted)',
            fontSize: 20, cursor: 'pointer', padding: 4, lineHeight: 1,
          }}>×</button>
        </div>

        {/* Form */}
        <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          {error && (
            <div style={{
              padding: 'var(--space-md)',
              background: 'var(--status-alert-bg)',
              border: '1px solid var(--red)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--red)',
              fontSize: 'var(--text-body)',
            }}>
              {error}
            </div>
          )}

          <div>
            <label style={labelStyle}>{t('fleet.registration')}</label>
            <input value={form.plate_number} onChange={e => set('plate_number', e.target.value)}
              placeholder="Ex: A 12345" style={inputStyle()} />
          </div>

          <div className="modal-form-row">
            <div>
              <label style={labelStyle}>{t('fleet.brand')}</label>
              <input value={form.make} onChange={e => set('make', e.target.value)}
                placeholder="Dacia" style={inputStyle()} />
            </div>
            <div>
              <label style={labelStyle}>{t('fleet.model')}</label>
              <input value={form.model} onChange={e => set('model', e.target.value)}
                placeholder="Logan" style={inputStyle()} />
            </div>
          </div>

          <div className="modal-form-row">
            <div>
              <label style={labelStyle}>{t('fleet.year')}</label>
              <input type="number" value={form.year} onChange={e => set('year', parseInt(e.target.value) || 2024)}
                style={inputStyle()} />
            </div>
            <div>
              <label style={labelStyle}>{t('fleet.category')}</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}
                style={{ ...inputStyle(), cursor: 'pointer' }}>
                <option value="economy">{t('fleet.economy')}</option>
                <option value="standard">{t('fleet.standard')}</option>
                <option value="premium">{t('fleet.premium')}</option>
                <option value="suv">{t('fleet.suv')}</option>
                <option value="luxury">{t('fleet.luxury')}</option>
              </select>
            </div>
          </div>

          <div className="modal-form-row">
            <div>
              <label style={labelStyle}>{t('fleet.color')}</label>
              <input value={form.couleur} onChange={e => set('couleur', e.target.value)}
                placeholder={t('fleet.colorPlaceholder')} style={inputStyle()} />
            </div>
            <div>
              <label style={labelStyle}>{t('fleet.pricePerDay')}</label>
              <input type="number" value={form.prix_jour} onChange={e => set('prix_jour', e.target.value)}
                placeholder="250" style={inputStyle()} />
            </div>
          </div>

          <div className="modal-form-row">
            <div>
              <label style={labelStyle}>{t('fleet.insuranceExpiry')}</label>
              <input type="date" value={form.insurance_expiry} onChange={e => set('insurance_expiry', e.target.value)}
                style={inputStyle()} />
            </div>
            <div>
              <label style={labelStyle}>{t('fleet.techVisitExpiry')}</label>
              <input type="date" value={form.tech_visit_expiry} onChange={e => set('tech_visit_expiry', e.target.value)}
                style={inputStyle()} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>{t('fleet.vignetteExpiry')}</label>
            <input type="date" value={form.vignette_expiry} onChange={e => set('vignette_expiry', e.target.value)}
              style={inputStyle()} />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: 'var(--space-lg) var(--space-xl)',
          borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-md)',
        }}>
          <button onClick={onClose} style={{
            padding: '10px 20px', height: 44,
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-body)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
            {t('common.cancel')}
          </button>
          <button onClick={handleSubmit} disabled={submitting || !apiReachable} style={{
            padding: '10px 24px', height: 44,
            background: (submitting || !apiReachable) ? 'var(--text-muted)' : 'var(--gold)',
            color: (submitting || !apiReachable) ? 'var(--text-secondary)' : 'var(--bg)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-body)',
            fontWeight: 700,
            cursor: (submitting || !apiReachable) ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.02em',
          }}>
            {!apiReachable ? t('system.write_blocked') : submitting ? t('common.creating') : t('common.add')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Zero-value summary fallback (API down = show nothing, not fake data) ──
const EMPTY_SUMMARY = { total: 0, available: 0, rented: 0, maintenance: 0, reserved: 0 };

// ── Main Component ───────────────────────────────────────────────────
export default function FleetManager() {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [branchId, setBranchId] = useState(null);
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const [sumRes, vehRes] = await Promise.allSettled([
        fleetApi.summary(),
        fleetApi.vehicles(),
      ]);

      if (ctrl.signal.aborted) return;

      if (sumRes.status === 'fulfilled' && sumRes.value) {
        setSummary(sumRes.value);
      }

      if (vehRes.status === 'fulfilled' && vehRes.value) {
        const list = vehRes.value?.vehicles || vehRes.value || [];
        setVehicles(Array.isArray(list) ? list : []);
        if (list.length > 0 && list[0].branch_id) {
          setBranchId(list[0].branch_id);
        }
      }

      if (sumRes.status === 'rejected' && vehRes.status === 'rejected') {
        setSummary(null);
        setVehicles([]);
        setError(sumRes.reason?.message || vehRes.reason?.message || t('system.serviceUnavailable'));
      } else {
        setError(null);
      }
    } catch (e) {
      if (!ctrl.signal.aborted) {
        setError(e.message);
        setSummary(null);
        setVehicles([]);
      }
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  const handleCreate = async (form) => {
    if (!branchId) {
      throw new Error(t('fleet.branchError'));
    }
    setSubmitting(true);
    try {
      await fleetApi.create({
        plate_number: form.plate_number.trim(),
        make: form.make.trim(),
        model: form.model.trim(),
        year: form.year,
        category: form.category,
        branch_id: branchId,
        current_mileage: 0,
        next_vidange_km: 5000,
        insurance_expiry: form.insurance_expiry || null,
        tech_visit_expiry: form.tech_visit_expiry || null,
        vignette_expiry: form.vignette_expiry || null,
        color: form.couleur || null,
        daily_rate_mad: form.prix_jour ? parseFloat(form.prix_jour) : null,
      });
      setModalOpen(false);
      fetchData();
    } finally {
      setSubmitting(false);
    }
  };

  // ── Local filtering + search ──────────────────────────────────────
  const filtered = vehicles.filter(v => {
    if (filter !== 'all' && v.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const haystack = `${v.plate_number} ${v.make} ${v.model}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const fs = summary || EMPTY_SUMMARY;

  // ── Filter counts ─────────────────────────────────────────────────
  const counts = {
    all: vehicles.length,
    available: fs.available || 0,
    rented: fs.rented || 0,
    maintenance: fs.maintenance || 0,
    reserved: fs.reserved || 0,
  };

  // ── Add button ────────────────────────────────────────────────────
  const addButton = (
    <button
      onClick={() => setModalOpen(true)}
      style={{
        padding: '10px 20px',
        height: 44,
        background: 'var(--gold)',
        color: 'var(--bg)',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--text-body)',
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: 'inherit',
        letterSpacing: '0.02em',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
      }}
    >
      + {t('common.add')}
    </button>
  );

  return (
    <div style={{ animation: 'fadeInUp 400ms var(--ease-spring) both' }}>
      <PageHeader
        title={t('fleet.title')}
        subtitle={`${vehicles.length} ${t('fleet.vehicles')}`}
        action={addButton}
      />

      {/* Filter bar */}
      <div className="lazy-section" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        marginBottom: 'var(--space-xl)',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-xs)', flex: 1, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <FilterPill
              key={f.key}
              label={t(f.labelKey)}
              count={counts[f.key]}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
            />
          ))}
        </div>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('fleet.searchPlaceholder')}
          style={{ width: 240, flexShrink: 0 }}
        />
      </div>

      {/* Error state */}
      {error && (
        <div style={{
          padding: 'var(--space-lg)',
          background: 'var(--status-alert-bg)',
          border: '1px solid var(--red)',
          borderRadius: 'var(--radius)',
          color: 'var(--red)',
          fontSize: 'var(--text-body)',
          marginBottom: 'var(--space-xl)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span>{t('common.errorPrefix')} {error}</span>
          <button
            onClick={() => { setError(null); setLoading(true); fetchData(); }}
            style={{
              padding: '6px 16px',
              background: 'transparent',
              border: '1px solid var(--red)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--red)',
              fontSize: 'var(--text-caption)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="grid-3col">
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonCard key={i} height={260} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && vehicles.length === 0 && (
        <Card>
          <EmptyState
            icon={null}
            title={t('fleet.noVehicles')}
            description={t('fleet.noVehiclesDesc')}
            ctaLabel={t('fleet.addVehicle')}
            onCta={() => setModalOpen(true)}
          />
        </Card>
      )}

      {/* No search results */}
      {!loading && filtered.length === 0 && vehicles.length > 0 && (
        <Card>
          <EmptyState
            icon={null}
            title={t('common.noResults')}
            description={`${t('common.noResults')}: "${search || t(FILTERS.find(f => f.key === filter)?.labelKey || 'fleet.all')}"`}
            ctaLabel={t('common.reset')}
            onCta={() => { setFilter('all'); setSearch(''); }}
          />
        </Card>
      )}

      {/* Vehicle grid */}
      {!loading && filtered.length > 0 && (
        <div className="grid-3col">
          {filtered.map((v, i) => (
            <VehicleCard key={v.id || i} vehicle={v} index={i} />
          ))}
        </div>
      )}

      {/* Add Vehicle Modal */}
      <AddVehicleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
        submitting={submitting}
      />
    </div>
  );
}
